import type { PrizeRoutingResult } from "@jpjj/solver-core/prize";
import { solvePrizeRouting } from "@jpjj/solver-core";
import { LEVELS } from "../game/levels";
import { state, requestFor } from "../game/state";
import { $ } from "../dom";
import type { Stage } from "../three/scene";

/* ---------- Per-room solves (rooms are fixed, so cache them) ---------- */
const solvePromises: (Promise<PrizeRoutingResult> | null)[] = LEVELS.map(() => null);
const solveResults: (PrizeRoutingResult | null)[] = LEVELS.map(() => null);

function solveRoom(i: number): Promise<PrizeRoutingResult> {
  let pr = solvePromises[i];
  if (!pr) {
    pr = solvePrizeRouting(requestFor(LEVELS[i])).then((res) => { solveResults[i] = res; return res; });
    solvePromises[i] = pr;
  }
  return pr;
}

/** Kick off all five solves during play, so the reveal is instant. */
export function prepareSolves(): Promise<PrizeRoutingResult[]> {
  return Promise.all(LEVELS.map((_, i) => solveRoom(i)));
}

function tripsPrize(levelIdx: number, trips: number[][]): number {
  const L = LEVELS[levelIdx];
  let p = 0;
  for (const t of trips) for (const n of t) if (n !== 0) p += L.loot[n - 1].prize;
  return p;
}

/* ---------- State ---------- */
let stage: Stage;
let cmpIndex = 0;
let animRunning = false;
let token = { cancelled: false };
function newToken(): { cancelled: boolean } {
  token.cancelled = true;
  token = { cancelled: false };
  return token;
}

export function openReveal(s: Stage): void {
  stage = s;
  document.body.classList.add("reveal-on");
  stage.setRevealMode(true);
  $("solverOverlay").classList.remove("hidden");
  $("endOverlay").classList.add("hidden");
  cmpIndex = 0;
  void runRoomCompare(0);
}

function setActiveView(which: "you" | "opt"): void {
  $("cmpViewYou").classList.toggle("active", which === "you");
  $("cmpViewOpt").classList.toggle("active", which === "opt");
}

async function runRoomCompare(i: number): Promise<void> {
  const tk = newToken();
  animRunning = true;
  cmpIndex = i;

  const L = LEVELS[i];
  const rec = state.records[i];
  stage.loadRoom(L);
  stage.setRevealMode(true);

  $("cmpHead").innerHTML = `Room ${i + 1} / ${LEVELS.length} — <span class="lv-room">⛏ ${L.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("optScore").textContent = "—";
  $("youScore").textContent = String(rec.prize);
  setActiveView("opt");

  const status = $("solverStatus");
  status.textContent = `Solving Room ${i + 1} with HiGHS…`;
  let res: PrizeRoutingResult;
  try {
    res = await solveRoom(i);
  } catch {
    status.textContent = "The solver could not be started — reload and try again.";
    animRunning = false;
    return;
  }
  if (tk.cancelled) return;
  status.textContent = res.status === "OPTIMAL"
    ? `HiGHS proved the best possible haul in ${Math.round(res.tSolveMs)} ms`
    : `Best haul found in ${Math.round(res.tSolveMs)} ms (not proven optimal)`;

  await stage.replayTrips(L, res.trips, tk, (n) => {
    $("optScore").textContent = String(tripsPrize(i, res.trips.slice(0, n)));
  });
  if (tk.cancelled) return;
  $("optScore").textContent = String(res.prize);
  finishRoom(rec.prize, res);
}

function finishRoom(youPrize: number, res: PrizeRoutingResult): void {
  animRunning = false;
  const proven = res.status === "OPTIMAL";
  const gap = res.prize - youPrize;
  const gapEl = $("cmpGap");
  if (gap <= 0) {
    gapEl.innerHTML = proven
      ? "You banked the <b>proven-best haul</b> — flawless looting."
      : "You matched (or beat) the best haul the solver found.";
  } else {
    const pct = youPrize > 0 ? ((gap / res.prize) * 100).toFixed(0) : "100";
    gapEl.innerHTML =
      `You banked <b>${youPrize}</b>; the ${proven ? "proven best" : "solver's best"} is ` +
      `<b>${res.prize}</b> — <b>${gap}</b> more gold (${pct}% more).`;
  }
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === LEVELS.length - 1 ? "See your run ▸" : "Next room ▸";
}

/** Toggle: replay the player's recorded route in 3D. */
export function viewYou(): void {
  if (animRunning) return;
  const tk = newToken();
  setActiveView("you");
  void stage.replayTrips(LEVELS[cmpIndex], state.records[cmpIndex].trips, tk);
}

/** Toggle: replay the optimizer's route in 3D. */
export function viewOpt(): void {
  if (animRunning) return;
  const res = solveResults[cmpIndex];
  if (!res) return;
  const tk = newToken();
  setActiveView("opt");
  void stage.replayTrips(LEVELS[cmpIndex], res.trips, tk);
}

/** Skip the running animation and snap to the optimizer's finished route. */
export function skipCompare(): void {
  if (!animRunning) return;
  newToken();
  const res = solveResults[cmpIndex];
  const rec = state.records[cmpIndex];
  if (!res) return;
  stage.showTripsInstant(LEVELS[cmpIndex], res.trips);
  $("optScore").textContent = String(res.prize);
  setActiveView("opt");
  finishRoom(rec.prize, res);
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < LEVELS.length - 1) void runRoomCompare(cmpIndex + 1);
  else showFinal();
}

function showFinal(): void {
  newToken();
  const youTotal = state.records.reduce((s, r) => s + r.prize, 0);
  const optTotal = solveResults.reduce((s, r) => s + (r ? r.prize : 0), 0);
  const allProven = solveResults.every((r) => r && r.status === "OPTIMAL");
  $("fcYou").textContent = String(youTotal);
  $("fcOpt").textContent = String(optTotal);
  $("fcSub").textContent = allProven ? "gold · proven best" : "gold · best found";
  const gap = optTotal - youTotal;
  $("fcGap").innerHTML = gap <= 0
    ? "A flawless run — across all five rooms you banked the best haul possible."
    : `Across five rooms you banked <b>${youTotal}</b> gold. The optimizer banks <b>${optTotal}</b> from the ` +
      `same loot and the same monster — <b>${gap}</b> more gold (+${((gap / Math.max(1, youTotal)) * 100).toFixed(0)}%).`;

  const rows = LEVELS.map((L, i) => {
    const o = solveResults[i];
    const ov = o ? o.prize : 0;
    const yv = state.records[i].prize;
    const pct = ov > 0 ? ((ov - yv) / ov) * 100 : 0;
    return `<tr><td>⛏ ${L.name}</td><td>${yv}</td><td class="gold">${ov}</td>` +
      `<td>${pct <= 0.5 ? "—" : "−" + pct.toFixed(0) + "%"}</td></tr>`;
  }).join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Room</th><th>You</th><th>Best</th><th>Gap</th></tr></thead><tbody>${rows}</tbody>`;

  $("solverOverlay").classList.add("hidden");
  $("endOverlay").classList.remove("hidden");
}

export function resetReveal(): void {
  newToken();
  animRunning = false;
  cmpIndex = 0;
  $("solverOverlay").classList.add("hidden");
  $("endOverlay").classList.add("hidden");
}
